import type { SignalLevel } from './SignalBars'

export type PhoneVisualState = 'normal' | 'in-call' | 'offline' | 'ringing'
export type PhoneCardSize = 'sm' | 'lg'

interface Props {
  state: PhoneVisualState
  size?: PhoneCardSize
  label?: string
  /** Kept on the API for callers that still pass quality; no longer rendered
   * — the corner signal bars looked off and were removed. */
  quality?: SignalLevel
}

const SIZE_PX: Record<PhoneCardSize, number> = { sm: 80, lg: 260 }

const STATE_LABEL: Record<PhoneVisualState, string> = {
  'normal': 'Ready',
  'in-call': 'In call',
  'offline': 'Offline',
  'ringing': 'Ringing',
}

/**
 * Pixel phone thumbnail with four visual states, adapted from /tmp/pixel-icons.html.
 * - normal: blue lock screen
 * - in-call: green screen with animated waveform
 * - offline: dark screen with "OFFLINE" label
 * - ringing: normal screen with a pulsing ring outline
 *
 * All animations honour `prefers-reduced-motion` via a global `@media
 * (prefers-reduced-motion: reduce)` rule in `src/index.css` that zeros the
 * `phone-endcall-pulse` / `phone-wave` / `phone-ring-pulse` keyframes.
 */
export function PhoneStateCard({ state, size = 'sm', label }: Props) {
  const width = SIZE_PX[size]
  const accessibleLabel = label ? `${label} — ${STATE_LABEL[state]}` : STATE_LABEL[state]

  return (
    <div
      className="relative inline-flex flex-col items-center gap-1"
      style={{ width }}
    >
      <svg
        viewBox="0 0 240 360"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={accessibleLabel}
        width={width}
        height={width * 1.5}
        className="block"
      >
        {state === 'normal' && <NormalPhone />}
        {state === 'ringing' && <RingingPhone />}
        {state === 'in-call' && <InCallPhone />}
        {state === 'offline' && <OfflinePhone />}
      </svg>

      {label && (
        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate max-w-full">
          {label}
        </div>
      )}
    </div>
  )
}

function NormalPhone() {
  return (
    <>
      <defs>
        <linearGradient id="bodyN" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#2a2f36" />
          <stop offset="1" stopColor="#15181d" />
        </linearGradient>
        <linearGradient id="screenN" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1e3a8a" />
          <stop offset="0.5" stopColor="#3b82f6" />
          <stop offset="1" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
      <rect x="20" y="10" width="200" height="340" rx="32" fill="url(#bodyN)" stroke="#0a0c0f" strokeWidth="2" />
      <rect x="218" y="90" width="4" height="34" rx="2" fill="#0a0c0f" />
      <rect x="218" y="138" width="4" height="22" rx="2" fill="#0a0c0f" />
      <rect x="30" y="20" width="180" height="320" rx="26" fill="url(#screenN)" />
      <circle cx="120" cy="36" r="5" fill="#0a0c0f" />
      <circle cx="120" cy="36" r="2" fill="#3b82f6" />
      <text x="120" y="120" textAnchor="middle" fontFamily="system-ui, Roboto, sans-serif" fontWeight="300" fontSize="44" fill="#fff">9:41</text>
      <text x="120" y="142" textAnchor="middle" fontFamily="system-ui, Roboto, sans-serif" fontSize="11" fill="#fff" opacity="0.85">Ready</text>
      <g fill="none" stroke="#ffffff" strokeWidth="2.4" opacity="0.85">
        <rect x="108" y="270" width="24" height="20" rx="3" fill="#ffffff" opacity="1" />
        <path d="M113 270 v-6 a7 7 0 0 1 14 0 v6" />
      </g>
      <rect x="92" y="328" width="56" height="4" rx="2" fill="#ffffff" opacity="0.7" />
    </>
  )
}

function RingingPhone() {
  return (
    <>
      <NormalPhone />
      <circle
        cx="120"
        cy="180"
        r="100"
        fill="none"
        stroke="#3b82f6"
        strokeWidth="4"
        opacity="0.7"
        className="phone-ring-pulse"
      />
    </>
  )
}

function InCallPhone() {
  return (
    <>
      <defs>
        <linearGradient id="bodyC" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#2a2f36" />
          <stop offset="1" stopColor="#15181d" />
        </linearGradient>
        <linearGradient id="screenC" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#064e3b" />
          <stop offset="1" stopColor="#10b981" />
        </linearGradient>
      </defs>
      <rect x="20" y="10" width="200" height="340" rx="32" fill="url(#bodyC)" stroke="#0a0c0f" strokeWidth="2" />
      <rect x="218" y="90" width="4" height="34" rx="2" fill="#0a0c0f" />
      <rect x="218" y="138" width="4" height="22" rx="2" fill="#0a0c0f" />
      <rect x="30" y="20" width="180" height="320" rx="26" fill="url(#screenC)" />
      <circle cx="120" cy="36" r="5" fill="#0a0c0f" />
      <circle cx="120" cy="36" r="2" fill="#34d399" />
      <circle cx="120" cy="120" r="38" fill="#ffffff" opacity="0.18" />
      <circle cx="120" cy="108" r="14" fill="#ffffff" />
      <path d="M92 152 Q120 128 148 152 L148 158 L92 158 Z" fill="#ffffff" />
      <text x="120" y="200" textAnchor="middle" fontFamily="system-ui, Roboto, sans-serif" fontSize="11" fill="#fff" opacity="0.85">Active</text>
      <g className="phone-wave" fill="#ffffff" opacity="0.9">
        {Array.from({ length: 15 }).map((_, i) => (
          <rect key={i} x={62 + i * 8} y="218" width="3" height="28" rx="1.5" />
        ))}
      </g>
      <g className="phone-endcall-pulse">
        <circle cx="120" cy="316" r="16" fill="#ef4444" />
        <path d="M108 316 q12 -10 24 0 l-3 4 q-9 -6 -18 0 z" fill="#fff" />
      </g>
    </>
  )
}

function OfflinePhone() {
  return (
    <>
      <defs>
        <linearGradient id="bodyI" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#1a1d22" />
          <stop offset="1" stopColor="#0d1015" />
        </linearGradient>
      </defs>
      <rect x="20" y="10" width="200" height="340" rx="32" fill="url(#bodyI)" stroke="#0a0c0f" strokeWidth="2" />
      <rect x="218" y="90" width="4" height="34" rx="2" fill="#0a0c0f" />
      <rect x="218" y="138" width="4" height="22" rx="2" fill="#0a0c0f" />
      <rect x="30" y="20" width="180" height="320" rx="26" fill="#0a0c0f" />
      <circle cx="120" cy="36" r="5" fill="#000" />
      <circle cx="120" cy="36" r="2" fill="#1f2937" />
      <circle cx="120" cy="180" r="20" fill="none" stroke="#3b4250" strokeWidth="2" />
      <rect x="118" y="168" width="4" height="12" rx="1.5" fill="#3b4250" />
      <text x="120" y="228" textAnchor="middle" fontFamily="system-ui, Roboto, sans-serif" fontSize="11" fill="#ef4444" letterSpacing="2">OFFLINE</text>
    </>
  )
}
